import { useState } from 'react';
import { Collapsible } from 'grommet';
import { CircleAlert, FormDown, FormUp } from 'grommet-icons';
import { TMissingDataResponse } from '/src/api/services/CalculateCreditsService';
import { Box } from '/src/components';
import { Card, CardBody, CardHeader, Text } from '/src/components/common';

type MissingDataSectionProps = {
  title: string;
  columns: string[];
  rows: string[][];
};

const ROWS_PER_PAGE = 50;

const ERROR_COLOR = 'status-critical';

const MissingDataSection = ({ title, columns, rows }: MissingDataSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(ROWS_PER_PAGE);
  const [isShowMoreHovered, setIsShowMoreHovered] = useState(false);

  if (rows.length === 0) return null;

  const visibleRows = rows.slice(0, visibleCount);
  const remainingCount = rows.length - visibleRows.length;

  const toggleOpen = () => {
    if (isOpen) {
      setVisibleCount(ROWS_PER_PAGE);
    }
    setIsOpen(!isOpen);
  };

  return (
    <Box border={{ side: 'bottom', color: 'light-4', size: '1px' }}>
      <Box
        direction="row"
        align="center"
        justify="between"
        pad={{ horizontal: 'medium', vertical: 'small' }}
        background={{ color: ERROR_COLOR, opacity: 'weak' }}
        onClick={toggleOpen}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        hoverIndicator={{ color: ERROR_COLOR, opacity: 'medium' }}
      >
        <Box direction="row" align="center" gap="small">
          <Text size="medium" weight={600} color={ERROR_COLOR}>
            {title}
          </Text>
          <Box background={ERROR_COLOR} round="xlarge" pad={{ horizontal: 'small', vertical: '2px' }}>
            <Text size="xsmall" weight={600} color="white">
              {rows.length}
            </Text>
          </Box>
        </Box>
        {isOpen ? <FormUp size="20px" color={ERROR_COLOR} /> : <FormDown size="20px" color={ERROR_COLOR} />}
      </Box>
      <Collapsible open={isOpen}>
        <Box pad={{ horizontal: 'medium', vertical: 'small' }}>
          <Box
            direction="row"
            gap="medium"
            pad={{ vertical: 'xsmall', horizontal: 'small' }}
            border={[
              { side: 'bottom', color: 'light-4', size: '1px' },
              { side: 'left', color: 'transparent', size: '2px' },
            ]}
          >
            {columns.map((column) => (
              <Box key={column} basis="0" flex="grow">
                <Text size="small" weight={600} color="dark-1">
                  {column}
                </Text>
              </Box>
            ))}
          </Box>
          <Box height={{ max: '300px' }} overflow="auto" gap="small" pad={{ top: 'small' }}>
            {visibleRows.map((row, index) => (
              <Box
                key={index}
                direction="row"
                gap="medium"
                height={{ min: '2rem' }}
                pad={{ vertical: 'xsmall', horizontal: 'small' }}
                border={{ side: 'left', color: ERROR_COLOR, size: '2px' }}
                align="center"
              >
                {row.map((value, columnIndex) => (
                  <Box key={columns[columnIndex]} basis="0" flex="grow">
                    <Text size="small" color="dark-2">
                      {value}
                    </Text>
                  </Box>
                ))}
              </Box>
            ))}
            {remainingCount > 0 && (
              <Box align="center" justify="center" margin={{ top: 'medium' }}>
                <Box
                  align="center"
                  justify="center"
                  onClick={() => setVisibleCount(visibleCount + ROWS_PER_PAGE)}
                  onMouseEnter={() => setIsShowMoreHovered(true)}
                  onMouseLeave={() => setIsShowMoreHovered(false)}
                  flex="shrink"
                >
                  <Text
                    size="small"
                    weight={600}
                    color="button-hover"
                    style={{ textDecoration: isShowMoreHovered ? 'underline' : 'none' }}
                  >
                    Show {Math.min(ROWS_PER_PAGE, remainingCount)} More ({remainingCount} remaining)
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Collapsible>
    </Box>
  );
};

export const CreditCalculationMissingDataPanel: React.FC<{
  missingData: TMissingDataResponse | null;
}> = ({ missingData }) => {
  if (missingData === null) return null;

  return (
    <Card
      margin={{ vertical: 'medium' }}
      background="white"
      elevation="none"
      round="xsmall"
      border={{ color: ERROR_COLOR, size: '1px' }}
    >
      <CardHeader
        pad={{ horizontal: 'medium', vertical: 'medium' }}
        border={{ side: 'bottom', color: 'light-4', size: '1px' }}
      >
        <Box direction="row" align="center" gap="small">
          <CircleAlert size="20px" color={ERROR_COLOR} />
          <Text size="large" weight="normal" color={ERROR_COLOR}>
            ERROR: Could not validate data
          </Text>
        </Box>
      </CardHeader>
      <CardBody pad="none" gap="none">
        <MissingDataSection
          title="The following client program reporting periods are already finalized"
          columns={['Client', 'Program']}
          rows={missingData.finalized_reporting_periods.map((item) => [item.client, item.program])}
        />
        <MissingDataSection
          title="Missing CIXD Standards"
          columns={['Program', 'Baseline Fuel Type']}
          rows={missingData.missing_cixd_standards.map((item) => [item.program, item.baseline_fuel_type])}
        />
        <MissingDataSection
          title="Missing Equipment Type EERs"
          columns={['Program', 'Equipment Type']}
          rows={missingData.missing_equipment_type_eers.map((item) => [item.program, item.equipment_type])}
        />
        <MissingDataSection
          title="Equipment Missing Fuel Pathways"
          columns={['Client', 'Facility', 'Serial Number']}
          rows={missingData.equipment_missing_fuel_pathways.map((item) => [
            item.client,
            item.facility,
            item.serial_number,
          ])}
        />
        <MissingDataSection
          title="Equipment Missing kWh"
          columns={['Client', 'Facility', 'Serial Number']}
          rows={missingData.equipment_missing_kwh.map((item) => [item.client, item.facility, item.serial_number])}
        />
      </CardBody>
    </Card>
  );
};
